import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';
import { brandHealthService } from '@/services/brandHealth.service';
import { selectedBusinessUnitIdAtom } from '@/stores/business-unit.store';
import type {
  BrandHealthDateRange,
  BrandHealthFilters,
  BrandHealthOverview,
  BrandHealthQuery,
} from '@/types/brandHealth';

interface QueryState<T> {
  data: T | undefined;
  isLoading: boolean;
  isError: boolean;
}

interface QueryResult<T> extends QueryState<T> {
  refetch: () => unknown;
}

export interface BrandHealthOverviewState extends QueryState<BrandHealthOverview> {
  refetch: () => void;
}

export interface BrandHealthFiltersState extends QueryState<BrandHealthFilters> {
  refetch: () => void;
}

const STALE_TIME = 60 * 1000;

function queryState<T>(result: QueryResult<T>, enabled: boolean): QueryState<T> & { refetch: () => void } {
  return {
    data: enabled ? result.data : undefined,
    isLoading: enabled ? result.isLoading : false,
    isError: enabled ? result.isError : false,
    refetch: () => { void result.refetch(); },
  };
}

function stableValues(values: string[] | undefined): string[] {
  return [...(values ?? [])].sort();
}

export function brandHealthOverviewQueryKey(query: BrandHealthQuery): readonly unknown[] {
  return [
    'sentinel',
    'brand-health',
    'overview',
    query.startDate,
    query.endDate,
    // The brand is part of the KEY, not only the request. Switching brands does not clear
    // the cache, so without it the second brand is served the first brand's report.
    query.buId ?? 'all',
    stableValues(query.channels),
    stableValues(query.categories),
  ];
}

export function useBrandHealthOverview(query: BrandHealthQuery | null): BrandHealthOverviewState {
  const startDate = query?.startDate;
  const endDate = query?.endDate;
  const channelsKey = JSON.stringify(stableValues(query?.channels));
  const categoriesKey = JSON.stringify(stableValues(query?.categories));
  // Read here rather than threaded in from the page: Brand Health is a reporting view, so
  // it follows the global switcher the way the Review Dashboard does.
  const buId = useAtomValue(selectedBusinessUnitIdAtom);
  const request = useMemo<BrandHealthQuery | null>(() => (
    startDate && endDate
      ? {
          startDate,
          endDate,
          buId,
          channels: JSON.parse(channelsKey) as string[],
          categories: JSON.parse(categoriesKey) as string[],
        }
      : null
  ), [startDate, endDate, buId, channelsKey, categoriesKey]);
  const queryAtom = useMemo(
    () => atomWithQuery(() => ({
      queryKey: request ? brandHealthOverviewQueryKey(request) : ['sentinel', 'brand-health', 'overview', 'disabled'],
      queryFn: async () => {
        if (!request) throw new Error('Brand Health overview query is disabled');
        return brandHealthService.getOverview(request);
      },
      enabled: request !== null,
      staleTime: STALE_TIME,
    })),
    [request],
  );
  const result = useAtomValue(queryAtom);
  return queryState(result, request !== null);
}

export function useBrandHealthFilters(range: BrandHealthDateRange | null): BrandHealthFiltersState {
  const startDate = range?.startDate;
  const endDate = range?.endDate;
  // The options have to be scoped too, or the dropdowns offer another brand's channels
  // and categories and selecting one returns an empty report.
  const buId = useAtomValue(selectedBusinessUnitIdAtom);
  const request = useMemo<BrandHealthDateRange | null>(
    () => startDate && endDate ? { startDate, endDate, buId } : null,
    [startDate, endDate, buId],
  );
  const queryAtom = useMemo(
    () => atomWithQuery(() => ({
      queryKey: request
        ? ['sentinel', 'brand-health', 'filters', request.startDate, request.endDate, request.buId ?? 'all']
        : ['sentinel', 'brand-health', 'filters', 'disabled'],
      queryFn: async () => {
        if (!request) throw new Error('Brand Health filters query is disabled');
        return brandHealthService.getFilters(request);
      },
      enabled: request !== null,
      staleTime: STALE_TIME,
    })),
    [request],
  );
  const result = useAtomValue(queryAtom);
  return queryState(result, request !== null);
}

export function useStableBrandHealthQuery(
  range: BrandHealthDateRange,
  channels: string[],
  categories: string[],
): BrandHealthQuery {
  const { startDate, endDate } = range;
  return useMemo(
    () => ({ startDate, endDate, channels: stableValues(channels), categories: stableValues(categories) }),
    [startDate, endDate, channels, categories],
  );
}
